import { Box, Typography } from '@mui/material';
import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import Routes, { CHECK_IN_TRAY_ID_PARAM, DELIVERY_ID_PARAM, SET_ID_PARAM } from 'routes';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useDelivery } from 'api/hooks/useDelivery';
import { useValidateTrayReference } from 'api/hooks/useValidateTrayReference';
import theme from 'App.theme';
import Button from 'components/Button';
import Card from 'components/Card/Card';
import Page from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import SavingPleaseWaitDialog from 'components/SavingPleaseWaitDialog';
import { useLanguage } from 'context/AppContext';
import { useCheckInRedirect } from 'hooks/useCheckInRedirect';
import { useCheckInTraysBySet } from 'hooks/useCheckInTraysBySet';
import { useSaveAndCompleteCheckIn } from 'hooks/useSaveAndCompleteCheckIn';
import { ConfirmAnotherDecantedTrayDialog } from './components/ConfirmAnotherDecantedTrayDialog';
import useSetDisplayLabel from 'hooks/useSetDisplayLabel';
import { useTrayBarcodeAssignmentSetting } from 'api/hooks/useTrayBarcodeAssignmentSetting';
import { CheckInTrayDialogs, VisibleDialog } from 'components/CheckInTrayDialogs';

export const CheckInTrayDecantDetails: FC = () => {
  const lang = useLanguage()[0].CheckInTrayDecantDetailsPage;
  const { deliveryId = '', setId: setKitInstanceId = '', checkInTrayId = '' } = useParams();

  const { data: delivery, isLoading: deliveryLoading } = useDelivery(deliveryId);
  const { data: currentUser } = useCurrentUser();

  const [checkInTraysBySet, setCheckInTraysBySet] = useCheckInTraysBySet();

  const [isSaving, setIsSaving] = useState(false);

  const [visibleDialog, setVisibleDialog] = useState<VisibleDialog | null>(null);

  const { saveAndComplete, isLoading: isSavingAndCompleting } = useSaveAndCompleteCheckIn();
  const navigate = useNavigate();

  const deliverySet = useMemo(
    () => delivery?.sets.find(set => set.kitInstanceId === setKitInstanceId),
    [delivery, setKitInstanceId],
  );
  const setName = useSetDisplayLabel(deliverySet);
  const set = checkInTraysBySet[setKitInstanceId];
  const tray = set?.trays[checkInTrayId];
  const trayName = tray?.name || '';
  const pageTitle = lang.title({
    setName,
    trayName,
    referenceNumber: '',
  });

  const { mutateAsync: validateTrayReference } = useValidateTrayReference();

  const { generateTrayBarcode } = useTrayBarcodeAssignmentSetting();

  const updateTrayNumber = useCallback(
    async (trayNum: number, assetNumber?: string) => {
      try {
        // Validate tray number at the surgery level to check if this new tray number is duplicating an existing tray number
        await validateTrayReference({ deliveryId, trayReference: trayNum });
      } catch (error) {
        // We already show a toast error message in the global error handler so we just need to return here
        return;
      }

      // Safely get the previous setKitInstanceId or initialize it
      const previousSetState = checkInTraysBySet[setKitInstanceId] || {
        trays: {},
      };

      // Check if the tray number already exists in the set
      if (previousSetState.trays) {
        for (const trayId in previousSetState.trays) {
          const tray = previousSetState.trays[trayId];
          if (
            (tray.decantedTrays && tray.decantedTrays.some(decantedTray => decantedTray.trayReference === trayNum)) ||
            tray.trayReference === trayNum
          ) {
            // do not let the user add the same tray number twice - show an error message and don't proceed with the save
            toast.error(lang.duplicateTrayNumberError);
            return;
          }
        }
      }

      setCheckInTraysBySet(prevCheckInTraysBySet => {
        // Safely get the previous setKitInstanceId or initialize it
        const previousSet = prevCheckInTraysBySet[setKitInstanceId] || {
          trays: {},
        };

        // Safely get the previous checkInTrayId or initialize it
        const previousTray = previousSet.trays[checkInTrayId] || {
          decantedTrays: [],
        };

        return {
          ...prevCheckInTraysBySet,
          [setKitInstanceId]: {
            ...previousSet,
            trays: {
              ...previousSet.trays,
              [checkInTrayId]: {
                ...previousTray,
                completed: true,
                decantedTrays: [
                  ...(previousTray.decantedTrays || []),
                  {
                    trayReference: trayNum ?? null,
                    assetNumber: generateTrayBarcode(deliveryId, trayNum, assetNumber),
                  },
                ],
              },
            },
          },
        };
      });

      if (!delivery) {
        throw new Error('No delivery found');
      }

      if (!currentUser) {
        throw new Error('No user found');
      }

      trackEvent(EventType.DecantTrayDetailsContinue, {
        delivery_id: deliveryId,
        order_id: delivery.orderNumber,
        current_user: currentUser.userId,
        tenant_name: currentUser.tenantName,
        set_id: setKitInstanceId,
        tray_name: trayName,
      });

      setVisibleDialog('ConfirmAnotherDecantedTray');
    },
    [
      checkInTrayId,
      checkInTraysBySet,
      currentUser,
      delivery,
      deliveryId,
      generateTrayBarcode,
      lang.duplicateTrayNumberError,
      setCheckInTraysBySet,
      setKitInstanceId,
      trayName,
      validateTrayReference,
    ],
  );

  useCheckInRedirect({
    deliveryId,
    setKitInstanceId,
    checkInTrayId,
    disabled: isSaving || !!visibleDialog,
  });

  useEffect(() => {
    // if the tray is not in the checkInTraysBySet, then the user has navigated to the page directly
    // and we should navigate them back to the check in page
    if (!tray) {
      navigate(Routes.CheckInWorkflow.path.replace(DELIVERY_ID_PARAM, deliveryId));
    }
  }, [deliveryId, navigate, tray]);

  const onBackClick = useCallback(() => {
    // unset the latest decanted image for this set and tray since the user is going back to retake the photo?
    const newCheckInTraysBySet = { ...checkInTraysBySet };

    const tray = newCheckInTraysBySet[setKitInstanceId]?.trays?.[checkInTrayId];
    if (tray?.decantedImages?.length && tray.decantedImages[tray.decantedImages.length - 1].dataUri) {
      tray.decantedImages.pop();

      setCheckInTraysBySet(newCheckInTraysBySet);
    }

    navigate(-1);
  }, [checkInTrayId, checkInTraysBySet, navigate, setCheckInTraysBySet, setKitInstanceId]);

  const onConfirmAnotherDecantedTray = useCallback(() => {
    navigate(
      Routes.CheckInTrayDecantImageCapture.path
        .replace(DELIVERY_ID_PARAM, deliveryId)
        .replace(SET_ID_PARAM, setKitInstanceId)
        .replace(CHECK_IN_TRAY_ID_PARAM, checkInTrayId),
    );
  }, [checkInTrayId, deliveryId, navigate, setKitInstanceId]);

  const onCancelAnotherDecantedTray = useCallback(async () => {
    if (!delivery) {
      throw new Error('No delivery found');
    }

    if (!currentUser) {
      throw new Error('No user found');
    }

    try {
      setIsSaving(true);
      setVisibleDialog(null);

      const checkInSet = checkInTraysBySet[setKitInstanceId];

      await saveAndComplete({
        checkInSet,
        setKitInstanceId,
        checkInTrayId,
        setVisibleDialog,
        isDecant: true,
      });
    } catch (error) {
      console.warn(error);
    } finally {
      setIsSaving(false);
    }
  }, [checkInTraysBySet, currentUser, delivery, saveAndComplete, setKitInstanceId, checkInTrayId]);

  return deliveryLoading ? (
    <PageSpinner />
  ) : (
    <>
      <Page>
        <Box>
          <PageHeader title={pageTitle} onBackClick={onBackClick}>
            <Button colors="active on white" onClick={onBackClick}>
              {lang.retakePhoto}
            </Button>

            <Button
              colors="white on active"
              loading={isSavingAndCompleting}
              disabled={isSavingAndCompleting}
              onClick={() => setVisibleDialog('TrayNumber')}
            >
              {lang.continue}
            </Button>
          </PageHeader>

          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                maxWidth: 480,
                width: '100%',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.layout.spacing.large,
              }}
            >
              <Typography variant="h6">{lang.heading}</Typography>

              <img
                // grab the last decanted tray image
                src={tray?.decantedImages && tray?.decantedImages[tray.decantedImages.length - 1]?.dataUri}
                alt={tray?.name}
                style={{
                  width: '100%',
                  height: 'auto',
                }}
              />
            </div>
          </Card>
        </Box>
      </Page>

      {'ConfirmAnotherDecantedTray' == visibleDialog && (
        <ConfirmAnotherDecantedTrayDialog
          onConfirm={onConfirmAnotherDecantedTray}
          onCancel={onCancelAnotherDecantedTray}
        />
      )}

      <CheckInTrayDialogs
        deliveryId={deliveryId || ''}
        orderRecordId={delivery?.orderRecordId || ''}
        noSets={!!delivery?.sets}
        visibleDialog={visibleDialog}
        setVisibleDialog={setVisibleDialog}
        onConfirm={updateTrayNumber}
      />

      {(isSaving || isSavingAndCompleting) && <SavingPleaseWaitDialog reason={'saving'} />}
    </>
  );
};
